import { setSnackbar } from '@/slices/components';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { DeviceTabProps } from '@features/deviceDataConfigurationModal/components/tabs/types';
import UPDATE_DEVICE_MATH_MODELING from '@features/deviceDataConfigurationModal/graphql/mutations/updateMathModeling';
import GET_MATH_MODELING_DATA from '@features/deviceDataConfigurationModal/graphql/queries/getMathModelingDeviceData';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { Controller, DefaultValues, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { FlowModelInput } from './FlowModelInput';
import { GCalculatedAngleModelInput } from './GCalculatedAngleModelInput';
import { LGFlowModelInput } from './LGFlowModelInput';

interface FormData {
  flowModel?: {
    expression: string;
  };
  calculatedAngleModel?: {
    expression: string;
  };
}

const MathModelingTab: FC<DeviceTabProps> = ({ deviceId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const dispatch = useDispatch();
  const { data } = useSuspenseQuery(GET_MATH_MODELING_DATA, {
    variables: { input: { id: deviceId } },
  });
  const [validators, setValidators] = useState<
    Record<'flow' | 'calculatedAngle', ((expression: string) => void) | null>
  >({
    flow: null,
    calculatedAngle: null,
  });
  const mathModelingData = data.device.dataConfiguration.mathModeling;
  const defaultValues: DefaultValues<FormData> = {
    flowModel: {
      expression: mathModelingData?.flowModel?.expression ?? '',
    },
    calculatedAngleModel: {
      expression: mathModelingData?.calculatedAngleModel?.expression ?? '',
    },
  };
  const [updateMathModeling] = useMutation(UPDATE_DEVICE_MATH_MODELING, {
    onCompleted: (data) => {
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`Dato actualizado correctamente`),
          severity: 'success',
        }),
      );
      methods.reset({
        flowModel: {
          expression: data.updateDeviceMathModeling.mathModeling?.flowModel?.expression ?? '',
        },
        calculatedAngleModel: {
          expression:
            data.updateDeviceMathModeling.mathModeling?.calculatedAngleModel?.expression ?? '',
        },
      });
    },
    onError: (errors) => {
      dispatch(
        setSnackbar({
          open: true,
          message: `${_(msg`Error al actualizar el dato`)}: ${errors.message}`,
          severity: 'error',
        }),
      );
    },
    update(cache) {
      cache.modify({
        id: cache.identify({ __typename: 'DeviceDataConfiguration', deviceId: deviceId }),
        fields: {
          radarParameters(_, { DELETE }) {
            return DELETE;
          },
        },
      });
    },
  });

  useEffect(() => {
    const mathModelingData = data.device.dataConfiguration.mathModeling;
    const defaultValues: DefaultValues<FormData> = {
      flowModel: {
        expression: mathModelingData?.flowModel?.expression ?? '',
      },
      calculatedAngleModel: {
        expression: mathModelingData?.calculatedAngleModel?.expression ?? '',
      },
    };
    methods.reset(defaultValues);
  }, [data]);

  const methods = useForm<FormData>({
    defaultValues,
  });

  useEffect(() => {
    setIsDirty?.(methods.formState.isDirty);
  }, [methods.formState.isDirty, setIsDirty]);

  const submitHandler = useMemo(
    () =>
      methods.handleSubmit(async (data) => {
        // validate flow expression
        if (data.flowModel?.expression) {
          if (!validators.flow) return;
          try {
            validators.flow(data.flowModel.expression);
          } catch (e) {
            if (e instanceof Error)
              methods.setError('flowModel.expression', {
                type: 'manual',
                message: _(msg`La expresión matemática es inválida: ${e.message}`),
              });
            return;
          }
        }

        // validate calculated angle expression
        if (data.calculatedAngleModel?.expression) {
          if (!validators.calculatedAngle) return;
          try {
            validators.calculatedAngle(data.calculatedAngleModel.expression);
          } catch (e) {
            if (e instanceof Error)
              methods.setError('calculatedAngleModel.expression', {
                type: 'manual',
                message: _(msg`La expresión matemática es inválida: ${e.message}`),
              });
            return;
          }
        }
        await updateMathModeling({
          variables: {
            input: {
              deviceId,
              mathModeling: {
                flowModel: data.flowModel
                  ? {
                      expression: data.flowModel.expression,
                    }
                  : undefined,
                calculatedAngleModel: data.calculatedAngleModel
                  ? {
                      expression: data.calculatedAngleModel.expression,
                    }
                  : undefined,
              },
            },
          },
        });
      }),
    [deviceId, methods, updateMathModeling, validators],
  );

  const isLG = data.device.profile.sensor === 'LG';
  const isLV = data.device.profile.sensor === 'LV';
  const isG = data.device.profile.sensor === 'G';

  const setFlowValidator = useCallback(
    (validator: (expression: string) => void) => {
      setValidators((prev) => ({
        ...prev,
        flow: validator,
      }));
    },
    [setValidators],
  );

  const setCalculatedAngleValidator = useCallback(
    (validator: (expression: string) => void) => {
      setValidators((prev) => ({
        ...prev,
        calculatedAngle: validator,
      }));
    },
    [setValidators],
  );

  return (
    <form onSubmit={submitHandler}>
      <DialogContent
        sx={{
          maxWidth: 'lg',
          minHeight: 500,
          margin: 'auto',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0,
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
        }}
      >
        <Typography variant="h5" mb={2}>
          <Trans>Modelamientos matemáticos</Trans>
        </Typography>
        {isLV && (
          <Controller
            name="flowModel.expression"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <FlowModelInput
                deviceId={deviceId}
                value={field.value}
                onChange={field.onChange}
                error={error}
                setValidator={setFlowValidator}
              />
            )}
          />
        )}
        {isLG && (
          <Controller
            name="flowModel.expression"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <LGFlowModelInput
                deviceId={deviceId}
                value={field.value}
                onChange={field.onChange}
                error={error}
                setValidator={setFlowValidator}
              />
            )}
          />
        )}
        {isG && (
          <Controller
            name="calculatedAngleModel.expression"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <GCalculatedAngleModelInput
                deviceId={deviceId}
                value={field.value}
                onChange={field.onChange}
                error={error}
                setValidator={setCalculatedAngleValidator}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ maxWidth: 'lg', margin: 'auto', py: 2 }}>
        <Button
          disabled={!methods.formState.isDirty}
          color="info"
          variant="outlined"
          onClick={() => methods.reset(defaultValues)}
        >
          <Trans>Restablecer Formulario</Trans>
        </Button>
        <Box flexGrow={1} />
        <Button color="info" variant="outlined" onClick={onClose}>
          <Trans>Cerrar</Trans>
        </Button>
        <LoadingButton
          disabled={!methods.formState.isDirty}
          loading={methods.formState.isSubmitting}
          type="submit"
          variant="contained"
        >
          <Trans>Guardar</Trans>
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

export default MathModelingTab;
